import React, { useState } from "react";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";

const ReportsTable = ({
  title,
  branch,
  dateRange,
  reportData,
  columns,
  exportEndpoint,
  onBack,
  emptyStateSubtitle = "No records found for the selected criteria.",
  groupBy,
  getRowClassName,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const pad2 = (num) => String(num).padStart(2, "0");

  const formatDateForFilename = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const dd = pad2(date.getDate());
    const MM = pad2(date.getMonth() + 1);
    const yyyy = date.getFullYear();
    const HH = pad2(date.getHours());
    const mm = pad2(date.getMinutes());
    const ss = pad2(date.getSeconds());
    return `${dd}-${MM}-${yyyy} ${HH}_${mm}_${ss}`;
  };

  const buildPreferredFilename = () => {
    const start = formatDateForFilename(dateRange.startDate);
    const end = formatDateForFilename(dateRange.endDate);
    // Ensure safe filename characters
    const safeTitle = (title || "Report").replace(/[/\\:*?"<>|]/g, "-");
    return `${safeTitle} [${start} - ${end}].csv`;
  };

  // Helper to escape CSV field values
  const escapeCSVField = (field) => {
    const stringField = String(field ?? "");
    if (stringField.includes(",") || stringField.includes('"') || stringField.includes("\n")) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  // Generate CSV content client-side from report data (used for "ALL" branches)
  const generateCSVContent = (rows) => {
    // CSV headers matching the backend format
    const headers = [
      "Reservation ID",
      "Status",
      "Monthly Billing",
      "Post?",
      "Entry Date",
      "Due Date",
      "Invoice / Bill Number",
      "Transaction Type",
      "Customer",
      "Supplier",
      "Tax Type",
      "Currency Code",
      "Item",
      "Description",
      "Qty",
      "Discount %",
      "Unit Price",
      "Account",
      "Location",
      "Class",
      "Tax Rate",
      "Payment Reference",
      "Payment Date",
      "Payment From Account",
      "Payment Amount",
      "Salesman",
      "Captain",
    ];

    const csvHeader = headers.join(",") + "\n";

    // Filter only locked rows for CSV (matching backend behavior)
    const lockedRows = rows.filter((row) => row.billingEventStatus === "locked");

    const csvRows = lockedRows
      .map((row) =>
        [
          row.reservationId || "",
          row.status || "",
          row.monthlyBilling || "",
          row.post || "",
          row.entryDate || "",
          row.dueDate || row.entryDate || "",
          row.invoiceNumber || "",
          row.transactionType || "",
          row.customer || "",
          row.supplier || "",
          row.taxType || "",
          row.currencyCode || "",
          row.item || "",
          row.description || "",
          row.qty || "",
          row.discount || "",
          row.unitPrice || "",
          row.account || "",
          row.location || "",
          row.class || "",
          row.taxRate || "",
          row.paymentReference || "",
          row.paymentDate || "",
          row.paymentFromAccount || "",
          row.paymentAmount || "",
          row.salesman || "",
          row.captain || "",
        ]
          .map(escapeCSVField)
          .join(",")
      )
      .join("\n");

    return csvHeader + csvRows;
  };

  // Download CSV content as a file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const filename = buildPreferredFilename();

      // If branch is "ALL", generate CSV client-side from the already-loaded merged data
      if (branch === "ALL") {
        if (!reportData?.rows || reportData.rows.length === 0) {
          alert("No data available for CSV export.");
          return;
        }

        const csvContent = generateCSVContent(reportData.rows);
        downloadCSV(csvContent, filename);
        return;
      }

      // Single branch: fetch CSV from backend
      const submitData = {
        branch: branch,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        format: "csv",
      };

      const response = await fetch(`${config.apiUrl}${exportEndpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCookie("_AT")}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Export error response:", errorData);
        alert("CSV export failed. Please try again.");
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Error exporting CSV. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const getBranchName = (branchId) => {
    if (branchId === "ALL") return "All Branches";
    return branchId === "1" ? "Lebanon" : branchId === "2" ? "UAE" : branchId;
  };

  const formatDateRange = (startDate, endDate) => {
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };
    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
  };

  // Precompute alternating background classes for consecutive groups
  const groupBackgroundClasses = (() => {
    if (!groupBy || !Array.isArray(reportData?.rows)) return [];
    const classes = [];
    let previousGroupValue;
    let useAltBackground = false; // ensure first detected group starts as gray after toggle
    for (const row of reportData.rows) {
      const currentGroupValue = typeof groupBy === "function" ? groupBy(row) : row?.[groupBy];
      if (currentGroupValue !== previousGroupValue) {
        useAltBackground = !useAltBackground;
        previousGroupValue = currentGroupValue;
      }
      classes.push(useAltBackground ? "bg-gray-50" : "bg-white");
    }
    return classes;
  })();

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
              <p className="text-gray-600 mt-1">
                Branch: {getBranchName(branch)} | Date Range: {formatDateRange(dateRange.startDate, dateRange.endDate)}
              </p>
              <p className="text-gray-600">
                Total Records: {reportData?.rows?.length || 0}, Total Reservations: {reportData?.summary?.totalReservations || 0}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExportCSV}
                disabled={isExporting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
              >
                {isExporting ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {isExporting ? "Exporting..." : "Export CSV"}
              </button>
              <button
                onClick={onBack}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {typeof col === "string" ? col : col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData?.rows?.map((row, rowIndex) => {
                  const defaultClass = groupBackgroundClasses[rowIndex] || "";
                  const rowClassName = getRowClassName ? getRowClassName(row, defaultClass) : `${defaultClass} hover:bg-gray-100`;
                  
                  return (
                  <tr key={rowIndex} className={rowClassName}>
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {typeof col === "string"
                          ? row[col] ?? ""
                          : (col.empty === true || (typeof col.empty === "function" && col.empty(row)))
                            ? ""
                            : col.render
                              ? col.render(row)
                              : col.accessor
                                ? row[col.accessor]
                                : ""}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(!reportData?.rows || reportData.rows.length === 0) && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data found</h3>
              <p className="mt-1 text-sm text-gray-500">{emptyStateSubtitle}</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ReportsTable;


